import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { SupportAgent, Email, Phone } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { SectionCard } from './ui';

interface SupportContact {
  level: 'dedicated' | 'priority' | 'standard';
  name: string;
  email: string;
  phone?: string;
  hours?: string;
  note: string;
  planName: string | null;
}

const LEVEL_LABEL: Record<SupportContact['level'], string> = {
  dedicated: 'Dedicated support',
  priority: 'Priority support',
  standard: 'Standard support',
};

/**
 * Who this store should contact for help, from its resolved entitlements.
 * Intelligence and Complete include the named dedicated contact; a Starter or
 * Pro store that bought the Dedicated Support add-on gets it too, because
 * `applyPurchasedAddonUnlocks` ORs `dedicatedSupport` onto its limits and
 * `resolveSupportContact` reads that boolean, not the tier.
 */
export const SupportContactCard: React.FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['support-contact'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/support-contact', { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || 'Could not load support contact');
      return body.data as SupportContact;
    },
    staleTime: 10 * 60 * 1000,
  });

  if (isLoading || isError || !data) return null;

  return (
    <SectionCard
      title="Support"
      leading={<SupportAgent color="primary" />}
      actions={
        <Chip
          label={LEVEL_LABEL[data.level]}
          size="small"
          color={data.level === 'dedicated' ? 'success' : data.level === 'priority' ? 'primary' : 'default'}
          variant={data.level === 'standard' ? 'outlined' : 'filled'}
        />
      }
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {data.level === 'dedicated' ? 'Your dedicated contact' : 'Contact'}
          </Typography>
          <Typography variant="body1" fontWeight={600}>{data.name}</Typography>
          {data.hours && (
            <Typography variant="body2" color="text.secondary">{data.hours}</Typography>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">{data.note}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="contained" startIcon={<Email />} href={`mailto:${data.email}`}>
            {data.email}
          </Button>
          {data.phone && (
            <Button size="small" variant="outlined" startIcon={<Phone />} href={`tel:${data.phone}`}>
              {data.phone}
            </Button>
          )}
        </Stack>
      </Stack>
    </SectionCard>
  );
};

export default SupportContactCard;
